import { browserFetch } from '../../services/browserSession';
// src/components/business/MyOffers.js

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { useAuth } from '../auth/AuthContext';
import { cancelCollabOffer, confirmStripeCheckoutSession, duplicateCollabOffer } from '../../services/api';
import {
    Search,
    CalendarDays,
    Users,
    Gem,
    Gift,
    MapPin,
    Pencil,
    Trash2,
    Copy,
    AlertTriangle,
    XCircle,
    Sparkles,
    LayoutList
} from 'lucide-react';
import { Button, CollabTypeBadge } from '../common/design-system';
import bizStyles from './MyOffers.module.scss';
import clsx from 'clsx';
import ImageWithFallback from '../common/ImageWithFallback';

// ─── Stat Item Component (Refined) ──────────────────────────
const OverviewStat = ({ label, value, helper }) => (
    <div className={bizStyles['stat-card']}>
        <label className={bizStyles['stat-label']}>{label}</label>
        <div className={bizStyles['stat-main']}>
            <span className={bizStyles['stat-number']}>{value}</span>
            {helper && <span className={bizStyles['stat-helper']}>{helper}</span>}
        </div>
    </div>
);

const formatDateRange = (fromDate, toDate) => {
    if (!fromDate && !toDate) return '--';
    const formatOptions = { day: 'numeric', month: 'short', year: 'numeric' };
    const start = fromDate ? new Date(fromDate).toLocaleDateString(undefined, formatOptions) : null;
    const end = toDate ? new Date(toDate).toLocaleDateString(undefined, formatOptions) : null;
    if (start && end) return `${start} - ${end}`;
    return start || end;
};

const stripMarkup = (value = '') => String(value).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const getOfferCapacity = (offer) => Number(offer.quantity || offer.max_applications || 0);

const getOfferFilterMatch = (offer, filter) => {
    if (filter === 'all') return true;
    if (filter === 'completed') return offer.lifecycle_status === 'completed';
    if (filter === 'active') return offer.status === 'active' && offer.lifecycle_status !== 'completed';
    if (filter === 'pending_payment') return offer.status === 'pending_payment' || offer.lifecycle_status === 'pending_payment';
    return offer.status === filter;
};

const getLifecycleBadgeClass = (offer, styles) => {
    if (offer.lifecycle_status === 'in_execution') return styles.execution;
    if (offer.lifecycle_status === 'ready_to_close') return styles.ready;
    if (offer.lifecycle_status === 'window_closed') return styles.closed;
    if (offer.status === 'active') return styles.active;
    if (offer.status === 'draft') return styles.draft;
    return styles.pending;
};

const MyOffers = () => {
    const { t } = useTranslation('collab');
    const navigate = useNavigate();
    const location = useLocation();
    const { subscriptionInfo, refreshSubscriptionInfo } = useAuth();

    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeFilter, setActiveFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    const [offerToDelete, setOfferToDelete] = useState(null);
    const [offerToCancel, setOfferToCancel] = useState(null);
    const [activatingOffers, setActivatingOffers] = useState(new Set());
    const [cancellingOffers, setCancellingOffers] = useState(new Set());
    const [duplicatingOffers, setDuplicatingOffers] = useState(new Set());

    const handleDuplicateOffer = async (offer) => {
        if (duplicatingOffers.has(offer.id)) return;
        try {
            setDuplicatingOffers(prev => new Set(prev).add(offer.id));
            await duplicateCollabOffer(offer.id);
            toast.success(t('list.messages.duplicate_success'));
            fetchOffersAndPlans();
         } catch (err) {
            toast.error(err.response?.data?.error || t('list.messages.duplicate_network_error'));
         } finally {
            setDuplicatingOffers(prev => { const n = new Set(prev); n.delete(offer.id); return n; });
         }
     };

    const fetchOffersAndPlans = useCallback(async () => {
        setLoading(true);
        try {

            const offersRes = await browserFetch('/api/collabs/collab/');

            if (offersRes.ok) {
                const data = await offersRes.json();
                setOffers(Array.isArray(data) ? data : data.results || []);
            }
        } catch (err) {
            console.error('Fetch error:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchOffersAndPlans(); }, [fetchOffersAndPlans]);

    // Stripe returns offer-based subscription checkouts here. The webhook is
    // asynchronous, so confirm the Checkout session from the browser as a
    // safe, idempotent fallback before refreshing the dashboard.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const sessionId = params.get('session_id');
        if (params.get('stripe') !== 'success' || !sessionId) return undefined;

        let cancelled = false;
        const confirmCheckout = async () => {
            try {
                await confirmStripeCheckoutSession(sessionId);
                if (cancelled) return;

                await Promise.all([fetchOffersAndPlans(), refreshSubscriptionInfo()]);
                toast.success(t('list.messages.payment_success'));

                params.delete('stripe');
                params.delete('session_id');
                const search = params.toString();
                navigate(`${location.pathname}${search ? `?${search}` : ''}`, { replace: true });
            } catch (err) {
                if (!cancelled) {
                    toast.error(err.response?.data?.error || t('list.messages.payment_error'));
                }
            }
        };

        confirmCheckout();
        return () => {
            cancelled = true;
        };
    }, [location.pathname, location.search, navigate, fetchOffersAndPlans, refreshSubscriptionInfo, t]);

    const handleActivateOffer = async (offerId, selectedPlanId = null) => {
        try {
             const offer = offers.find(o => o.id === offerId);
             if (offer && (['paid', 'hybrid'].includes(offer.compensation_mode) || offer.sponsor_this_offer)) {
                 navigate(`/business/collabs/${offerId}/summary`);
                 return;
             }
             if (offer && offer.exceeds_plan_limit) {
                 toast.error(t('list.messages.limit_exceeded'));
                 return;
             }

            if (selectedPlanId) {
                navigate(`/business/plans?offerId=${offerId}`);
                return;
            }

            await refreshSubscriptionInfo();
            if (subscriptionInfo?.has_active_subscription && (subscriptionInfo.unlimited_collabs || subscriptionInfo.available_collabs > 0)) {
                await activateOfferDirectly(offerId);
            } else {
                navigate(`/business/plans?offerId=${offerId}`);
             }
         } catch (err) { toast.error(t('list.messages.activate_error')); }
     };

    const activateOfferDirectly = async (offerId) => {
        if (activatingOffers.has(offerId)) return;
        try {
            setActivatingOffers(prev => new Set(prev).add(offerId));

            const res = await browserFetch(`/api/collabs/collab/${offerId}/publish/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });
             if (res.ok) {
                 toast.success(t('list.messages.activate_success'));
                 fetchOffersAndPlans();
                refreshSubscriptionInfo();
             } else {
                 const err = await res.json();
                 toast.error(err.error || t('list.messages.activate_error'));
             }
         } finally {
            setActivatingOffers(prev => { const n = new Set(prev); n.delete(offerId); return n; });
        }
    };

    const handleDeleteOffer = async () => {
        if (!offerToDelete) return;
        try {

            const res = await browserFetch(`/api/collabs/collab/${offerToDelete.id}/`, {
                method: 'DELETE',
                headers: { }
            });
             if (res.ok) {
                 toast.success(t('list.messages.delete_success'));
                 fetchOffersAndPlans();
                 setOfferToDelete(null);
             } else {
                 const err = await res.json();
                 toast.error(err.error || t('list.messages.delete_error'));
             }
         } catch (err) { toast.error(t('list.messages.delete_error')); }
     };

    const handleCancelOffer = async () => {
        if (!offerToCancel || cancellingOffers.has(offerToCancel.id)) return;
        try {
            setCancellingOffers(prev => new Set(prev).add(offerToCancel.id));
            const response = await cancelCollabOffer(offerToCancel.id);
            toast.success(response.data?.message || t('list.messages.cancel_success'));
            fetchOffersAndPlans();
            refreshSubscriptionInfo();
            setOfferToCancel(null);
        } catch (err) {
            toast.error(err.response?.data?.error || t('list.messages.cancel_error'));
        } finally {
            setCancellingOffers(prev => { const n = new Set(prev); n.delete(offerToCancel.id); return n; });
        }
    };

    const filteredList = useMemo(() => {
        return offers.filter(o => {
            const isTabMatch = getOfferFilterMatch(o, activeFilter);
            const haystack = `${o.title || ''} ${o.country_name || ''} ${o.country || ''}`.toLowerCase();
            const isSearchMatch = haystack.includes(searchTerm.trim().toLowerCase());
            return isTabMatch && isSearchMatch;
        });
    }, [offers, activeFilter, searchTerm]);

    const filterCounts = useMemo(() => ({
        all: offers.length,
        active: offers.filter((offer) => getOfferFilterMatch(offer, 'active')).length,
        pending_payment: offers.filter((offer) => getOfferFilterMatch(offer, 'pending_payment')).length,
        draft: offers.filter((offer) => getOfferFilterMatch(offer, 'draft')).length,
        cancelled: offers.filter((offer) => getOfferFilterMatch(offer, 'cancelled')).length,
        completed: offers.filter((offer) => getOfferFilterMatch(offer, 'completed')).length,
    }), [offers]);

    if (loading) return (
         <div className={bizStyles['loading-workspace']}>
             <div className={bizStyles.spinner} />
             <p>{t('list.loading')}</p>
         </div>
    );

    return (
        <div className={bizStyles['my-offers-workspace']}>
            <div className={bizStyles['workspace-hero']}>
                <div className={bizStyles.container}>
                    <div className={bizStyles['hero-layout']}>
                         <div className={bizStyles['hero-content']}>
                             <div className={bizStyles['hero-badge']}>
                                 <Sparkles size={12} />
                                 Business Dashboard
                             </div>
                             <div className={bizStyles['title-group']}>
                                 <h1>{t('list.title')}</h1>
                             </div>
                             <p className={bizStyles.lead}>{t('list.subtitle')}</p>

                             <div className={bizStyles['stats-grid']}>
                                 <OverviewStat
                                     label={t('list.stats.active')}
                                     value={offers.filter(o => getOfferFilterMatch(o, 'active')).length}
                                     helper={t('list.stats.active_help')}
                                 />
                                 <OverviewStat
                                     label={t('list.stats.drafts')}
                                     value={offers.filter(o => o.status === 'draft' || o.status === 'pending_payment').length}
                                     helper={t('list.stats.drafts_help')}
                                 />
                                 <OverviewStat
                                     label={t('list.stats.applications')}
                                     value={offers.reduce((a, c) => a + (c.applied_creators || 0), 0)}
                                     helper={t('list.stats.applications_help')}
                                 />
                             </div>
                         </div>

                         <div className={bizStyles['hero-actions']}>
                             <div className={bizStyles['result-count']}>
                                 <strong>{filteredList.length}</strong>
                                 <span>{activeFilter === 'all' ? t('list.filters.all').toLowerCase() : t(`list.filters.${activeFilter === 'pending_payment' ? 'pending' : activeFilter}`, activeFilter).toLowerCase()}</span>
                             </div>
                             <div className={bizStyles['create-actions']}>
                               <Button variant="outline" size="lg" onClick={() => navigate('/business/create-paid-collab')}>
                                   <Gem size={18} className="mr-2" /> Paid collab
                               </Button>
                               <Button variant="primary" size="lg" onClick={() => navigate('/business/create-collab')}>
                                   <Gift size={20} className="mr-2" /> {t('list.btn_new', 'Gifting collab')}
                               </Button>
                             </div>
                         </div>
                    </div>
                </div>
            </div>

            <div className={bizStyles.container}>
                <div className={bizStyles['controls-bar']}>
                    <div className={bizStyles['tabs-pill']}>
                         {[
                             { id: 'all', label: t('list.filters.all') },
                             { id: 'active', label: t('list.filters.active') },
                             { id: 'pending_payment', label: t('list.filters.pending') },
                             { id: 'draft', label: t('list.filters.draft') },
                             { id: 'completed', label: t('list.filters.completed', 'Completadas') },
                             { id: 'cancelled', label: t('list.filters.cancelled') }
                         ].map(f => (
                            <button
                                key={f.id}
                                className={clsx(activeFilter === f.id && bizStyles.active)}
                                onClick={() => setActiveFilter(f.id)}
                             >
                                 <span>{f.label}</span>
                                 <small>{filterCounts[f.id]}</small>
                             </button>
                        ))}
                    </div>

                     <div className={bizStyles['search-wrapper']}>
                         <Search size={16} />
                         <input
                             type="text"
                             placeholder={t('list.search_placeholder')}
                             value={searchTerm}
                             onChange={(e) => setSearchTerm(e.target.value)}
                         />
                     </div>
                </div>

                <div className={bizStyles['apps-grid']}>
                    <AnimatePresence mode="popLayout">
                        {filteredList.length > 0 ? (
                             filteredList.map(offer => {
                                 const capacity = getOfferCapacity(offer);
                                 const applicants = Number(offer.applied_creators || 0);
                                 const progress = capacity > 0 ? Math.min(100, Math.round((applicants / capacity) * 100)) : 0;
                                 const description = stripMarkup(offer.description);
                                 return (
                                 <motion.div
                                    key={offer.id}
                                    layout
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.95 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <div
                                        className={bizStyles['biz-collab-card']}
                                        onClick={() => navigate(`/business/collabs/${offer.id}/journey`)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(event) => {
                                          if (event.key === 'Enter' || event.key === ' ') {
                                            event.preventDefault();
                                            navigate(`/business/collabs/${offer.id}/journey`);
                                          }
                                        }}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <div className={bizStyles['card-top']}>
                                            <div className={bizStyles['visual-wrap']}>
                                                <ImageWithFallback
                                                    src={offer.featured_image_url || offer.featured_image}
                                                    alt={offer.title}
                                                    name={offer.title}
                                                    className={bizStyles['collab-img']}
                                                />
                                            </div>

                                            <div className={bizStyles['info-overlay']}>
                                              <div className={bizStyles['top-tags']}>
                                                     <div className={bizStyles['actions-mini']}>
                                                         <button
                                                             onClick={(e) => { e.stopPropagation(); handleDuplicateOffer(offer); }}
                                                             title={t('list.card.duplicate')}
                                                             aria-label={t('list.card.duplicate')}
                                                             disabled={duplicatingOffers.has(offer.id)}
                                                         >
                                                             <Copy size={14} />
                                                         </button>
                                                         {offer.can_delete !== false ? (
                                                             <button onClick={(e) => { e.stopPropagation(); setOfferToDelete(offer); }} title={t('list.card.delete')} aria-label={t('list.card.delete')} className={bizStyles.danger}><Trash2 size={14} /></button>
                                                         ) : offer.status === 'active' ? (
                                                             <button
                                                                 onClick={(e) => { e.stopPropagation(); if (offer.can_cancel !== false) setOfferToCancel(offer); }}
                                                                 title={offer.can_cancel === false ? t('list.card.cancel_locked') : t('list.card.cancel')}
                                                                 aria-label={offer.can_cancel === false ? t('list.card.cancel_locked') : t('list.card.cancel')}
                                                                 className={bizStyles.danger}
                                                                 disabled={offer.can_cancel === false || cancellingOffers.has(offer.id)}
                                                             >
                                                                 <XCircle size={14} />
                                                             </button>
                                                         ) : null}
                                                     </div>
                                                 </div>
                                            </div>

                                            {/* Date + budget strip */}
                                            <div className={bizStyles['image-strip']}>
                                                <div className={bizStyles['strip-item']}>
                                                    <CalendarDays size={12} />
                                                     <span className={bizStyles['strip-value']}>{formatDateRange(offer.from_date, offer.to_date)}</span>
                                                </div>
                                                <div className={bizStyles['strip-item']}>
                                                    <Gem size={12} />
                                                     <span className={bizStyles['strip-value']}>{['paid', 'hybrid'].includes(offer.compensation_mode)
                                                      ? `${offer.cash_currency || 'USD'} ${Number(offer.cash_compensation || offer.sponsor_amount || 0).toLocaleString()}`
                                                      : `$${Number(offer.value || 0).toLocaleString()}`}</span>
                                                </div>
                                            </div>
                                        </div>

                                         <div className={bizStyles['card-body']}>
                                             <div className={bizStyles['card-badges-row']}>
                                                <div className={clsx(
                                                 bizStyles['status-badge'],
                                                 getLifecycleBadgeClass(offer, bizStyles)
                                             )}>
                                                 <span className={bizStyles['status-dot']} />
                                                 {offer.lifecycle_status ? t(`lifecycle.${offer.lifecycle_status}`, offer.lifecycle_status_display) : (offer.status === 'active' ? t('list.card.status_active') : offer.status === 'draft' ? t('list.card.status_draft') : t('list.card.status_pending'))}
                                             </div>
                                                <CollabTypeBadge offer={offer} size="sm" />
                                            </div>
                                             {offer.status === 'active' && offer.is_feed_visible === false && (
                                                 <div className={bizStyles['feed-hidden-note']}>
                                                     {t('list.card.feed_hidden_note')}
                                                 </div>
                                             )}
                                             <h3>{offer.title || t('list.card.no_title')}</h3>
                                             <p className={bizStyles.description}>
                                                 {description ? `${description.substring(0, 120)}${description.length > 120 ? '…' : ''}` : t('list.card.no_description', 'Sin descripción disponible.')}
                                            </p>

                                             {/* Applications row */}
                                             <div className={bizStyles['applications-row']}>
                                                 <div className={bizStyles['app-label']}>
                                                     <Users size={14} />
                                                     <span>
                                                          <span className={bizStyles['app-count']}>{applicants}</span>
                                                          {capacity ? `/${capacity}` : ''}
                                                     </span>
                                                     <span>{t('list.card.applicants')}</span>
                                                 </div>
                                                  {capacity > 0 && (
                                                     <span className={bizStyles['spots-pill']}>
                                                         {t('list.card.limited_spots')}
                                                     </span>
                                                 )}
                                             </div>

                                             {/* Progress bar */}
                                              {capacity > 0 && (
                                                 <div className={bizStyles['progress-wrap']}>
                                                     <div className={bizStyles['progress-track']}>
                                                         <div
                                                             className={bizStyles['progress-fill']}
                                                              style={{ width: `${progress}%` }}
                                                         />
                                                     </div>
                                                 </div>
                                             )}

                                             {/* Compact location */}
                                             <div className={bizStyles['meta-compact']}>
                                                 <span><MapPin size={11} /> {offer.country_name || offer.country || 'Global'}</span>
                                             </div>
                                         </div>

                                        <div className={bizStyles['card-footer']}>
                                            <span className={bizStyles['action-tooltip']} title={offer.can_edit === false ? t('list.card.edit_locked') : ''}>
                                                <Button
                                                    variant="outline"
                                                    size="md"
                                                    aria-disabled={offer.can_edit === false}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (offer.can_edit === false) {
                                                            const isPaidAndFunded = ['paid', 'hybrid'].includes(offer.compensation_mode) && ['paid', 'partially_refunded', 'fully_refunded', 'disputed'].includes(offer.paid_collab_payment_status);
                                                            toast.info(
                                                                isPaidAndFunded
                                                                    ? t('review.edit_locked_paid', 'Las colaboraciones pagadas no pueden editarse una vez financiadas.')
                                                                    : t('list.card.edit_locked', 'Esta colaboración no se puede editar porque ya fue financiada o tiene entregas en curso.')
                                                            );
                                                            return;
                                                        }
                                                        navigate(`/business/collabs/${offer.id}/edit`);
                                                    }}
                                                >
                                                    <Pencil size={14} /> {t('list.card.edit')}
                                                </Button>
                                            </span>

                                            {offer.can_delete !== false ? (
                                                <Button
                                                    variant="primary"
                                                    size="md"
                                                    onClick={(e) => { e.stopPropagation(); setOfferToDelete(offer); }}
                                                >
                                                     <Trash2 size={14} /> {t('list.card.delete')}
                                                </Button>
                                            ) : offer.status === 'active' ? (
                                                <>
                                                    <span className={bizStyles['action-tooltip']} title={offer.can_cancel === false ? t('list.card.cancel_locked') : ''}>
                                                        <Button
                                                            variant="outline"
                                                            size="md"
                                                            disabled={offer.can_cancel === false || cancellingOffers.has(offer.id)}
                                                            onClick={(e) => { e.stopPropagation(); setOfferToCancel(offer); }}
                                                        >
                                                             <XCircle size={14} /> {t('list.card.cancel')}
                                                        </Button>
                                                    </span>
                                                    <Button
                                                        variant="primary"
                                                         size="md"
                                                         onClick={(e) => { e.stopPropagation(); navigate(`/business/collabs/${offer.id}/journey`); }}
                                                     >
                                                         {t('list.card.open_collab', 'Abrir collab')}
                                                     </Button>
                                                </>
                                            ) : offer.status === 'cancelled' ? (
                                                <Button variant="outline" size="md" disabled>
                                                    {t('list.card.status_cancelled')}
                                                </Button>
                                            ) : (
                                                <Button
                                                    variant="primary"
                                                     size="md"
                                                     disabled={activatingOffers.has(offer.id)}
                                                     onClick={(e) => { e.stopPropagation(); handleActivateOffer(offer.id); }}
                                                 >
                                                     {activatingOffers.has(offer.id)
                                                       ? t('list.card.activating')
                                                       : (['paid', 'hybrid'].includes(offer.compensation_mode) || offer.sponsor_this_offer)
                                                         ? t('list.card.fund_and_publish')
                                                         : t('list.card.activate')}
                                                 </Button>
                                            )}
                                        </div>
                                    </div>
                                </motion.div>
                                 );
                             })
                        ) : !(searchTerm || activeFilter !== 'all') ? (
                            <motion.div
                                className={bizStyles['first-activation-hero']}
                                initial={{ opacity: 0, y: 25, scale: 0.98 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                            >
                                <div className={bizStyles['hero-glow']} aria-hidden="true" />
                                <div className={bizStyles['hero-badge']}>
                                    <Sparkles size={14} />
                                    <span>Primera activación de negocio</span>
                                </div>
                                <h2>Convierte tu primera idea en una colaboración con creadores</h2>
                                <p>
                                    Tu negocio ya está verificado en FOTH. Diseñamos un flujo guiado para que elijas entre Gifting o Paid Collab y lances tu primera campaña en minutos con ayuda de FOTH AI.
                                </p>
                                <div className={bizStyles['hero-actions']}>
                                    <Button
                                        variant="primary"
                                        size="lg"
                                        className={bizStyles['btn-start-first']}
                                        onClick={() => navigate('/business/first-collab')}
                                    >
                                        <span>Crear mi primer Collab</span>
                                        <Sparkles size={17} />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="lg"
                                        onClick={() => navigate('/business/first-collab')}
                                    >
                                        Ver cómo funciona
                                    </Button>
                                </div>
                                <div className={bizStyles['hero-proof']}>
                                    <div className={bizStyles['proof-pill']}>
                                        <Gift size={14} />
                                        <span>Gifting o Paid Collabs</span>
                                    </div>
                                    <div className={bizStyles['proof-pill']}>
                                        <Sparkles size={14} />
                                        <span>Asistente FOTH AI</span>
                                    </div>
                                    <div className={bizStyles['proof-pill']}>
                                        <Gem size={14} />
                                        <span>Escrow y pagos protegidos</span>
                                    </div>
                                </div>
                            </motion.div>
                        ) : (
                            <motion.div
                                className={bizStyles['empty-placeholder']}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                            >
                                <div className={bizStyles['icon-box']}>
                                    <LayoutList size={40} />
                                </div>
                                 <h2>{t('list.empty.no_match')}</h2>
                                 <p>{t('list.empty.no_match_help')}</p>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            {/* DELETE MODAL */}
            <AnimatePresence>
                {offerToDelete && (
                    <div className={bizStyles['modal-overlay']}>
                        <div className={bizStyles['delete-modal']}>
                             <div className={bizStyles.header}>
                                 <div className={bizStyles['warn-icon']}><AlertTriangle size={32} /></div>
                                 <h2>{t('list.delete_modal.title')}</h2>
                             </div>
                             <p>{t('list.delete_modal.message', { title: offerToDelete.title })}</p>
                             <p className={bizStyles['fairness-warning']}>{t('list.delete_modal.fairness_warning')}</p>
                             <div className={bizStyles['modal-btns']}>
                                 <Button variant="outline" onClick={() => setOfferToDelete(null)}>{t('list.delete_modal.cancel')}</Button>
                                 <Button className={bizStyles['btn-delete']} onClick={handleDeleteOffer}>{t('list.delete_modal.confirm')}</Button>
                             </div>
                        </div>
                    </div>
                )}
            </AnimatePresence>

            {/* CANCEL MODAL */}
            <AnimatePresence>
                {offerToCancel && (
                    <div className={bizStyles['modal-overlay']}>
                        <div className={bizStyles['delete-modal']}>
                             <div className={bizStyles.header}>
                                 <div className={bizStyles['warn-icon']}><AlertTriangle size={32} /></div>
                                 <h2>{t('list.cancel_modal.title')}</h2>
                             </div>
                             <p>{t('list.cancel_modal.message', { title: offerToCancel.title })}</p>
                             <p className={bizStyles['fairness-warning']}>{t('list.cancel_modal.fairness_warning')}</p>
                             <div className={bizStyles['modal-btns']}>
                                 <Button variant="outline" onClick={() => setOfferToCancel(null)}>{t('list.cancel_modal.keep')}</Button>
                                 <Button
                                     className={bizStyles['btn-delete']}
                                     disabled={cancellingOffers.has(offerToCancel.id)}
                                     onClick={handleCancelOffer}
                                 >
                                     {cancellingOffers.has(offerToCancel.id) ? t('list.cancel_modal.cancelling') : t('list.cancel_modal.confirm')}
                                 </Button>
                             </div>
                        </div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default MyOffers;
